/**
 * FlagFraudModal - Admin Portal Component
 * 
 * Modal for flagging promo codes as fraudulent.
 * Requires SuperAdmin authorization and detailed fraud reason.
 * Sends alert to SuperAdmin team.
 */

import React, { useState } from 'react';
import './FlagFraudModal.css';

const LAMBDA_URL = 'https://fytckhfpoh5wgzodartzjtse640ueugs.lambda-url.ap-southeast-1.on.aws';

const FlagFraudModal = ({ isOpen, onClose, code, onSuccess }) => {
  const [fraudReason, setFraudReason] = useState('');
  const [suspendUsers, setSuspendUsers] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleFlagFraud = async () => {
    if (!fraudReason.trim() || fraudReason.trim().length < 20) {
      setError('Fraud reason must be at least 20 characters');
      return;
    }

    if (!confirmed) {
      setError('Please confirm the fraud flag action');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get ID token from Cognito
      const { getIdToken } = await import('aws-amplify/auth');
      const { idToken } = (await getIdToken()).tokens;

      const response = await fetch(`${LAMBDA_URL}/flag-fraud`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          code: code.code,
          reason: fraudReason.trim(),
          suspendUsers
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to flag fraud');
      }

      const data = await response.json();
      
      // Success
      if (onSuccess) {
        onSuccess(data.code);
      }
      
      handleClose();
    } catch (err) {
      console.error('Flag fraud error:', err);
      setError(err.message || 'Failed to flag fraud');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFraudReason('');
    setSuspendUsers(false);
    setConfirmed(false);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content modal-content--fraud" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header modal-header--fraud">
          <h3 className="modal-title">⚠️ Flag as Fraud</h3>
          <button className="modal-close" onClick={handleClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-body">
          <div className="modal-danger">
            <div className="modal-danger__icon">🚨</div>
            <div className="modal-danger__text">
              <strong>Critical Action:</strong> This will permanently mark the code as fraudulent and revoke it. This action cannot be undone.
            </div>
          </div>

          <div className="modal-code-info modal-code-info--fraud">
            <div className="modal-code-info__label">Promo Code:</div>
            <div className="modal-code-info__value">{code.code}</div>
          </div>

          {code.redemptions && code.redemptions.length > 0 && (
            <div className="modal-redemptions">
              <h4 className="modal-redemptions__title">
                Redemption History ({code.redemptions.length})
              </h4>
              <div className="modal-redemptions__list">
                {code.redemptions.map((redemption, index) => (
                  <div key={index} className="modal-redemption-item">
                    <div className="modal-redemption-item__email">
                      {redemption.userEmail}
                    </div>
                    <div className="modal-redemption-item__date">
                      {new Date(redemption.redeemedAt).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="modal-form-group">
            <label className="modal-label" htmlFor="fraud-reason">
              Fraud Reason <span className="modal-required">*</span>
            </label>
            <textarea
              id="fraud-reason"
              className="modal-textarea"
              placeholder="Provide detailed explanation of fraudulent activity (minimum 20 characters)"
              value={fraudReason}
              onChange={(e) => setFraudReason(e.target.value)}
              rows={5}
              disabled={loading}
            />
            <div className="modal-char-count">
              {fraudReason.length} / 20 minimum
            </div>
          </div>

          <div className="modal-checkbox-group">
            <label className="modal-checkbox">
              <input
                type="checkbox"
                checked={suspendUsers}
                onChange={(e) => setSuspendUsers(e.target.checked)}
                disabled={loading}
              />
              <span>Suspend user accounts associated with this code (optional)</span>
            </label>
          </div>

          <div className="modal-checkbox-group">
            <label className="modal-checkbox">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                disabled={loading}
              />
              <span>
                <strong>I confirm this is fraudulent activity</strong> and understand this will:
                <ul className="modal-checkbox__list">
                  <li>Permanently revoke the promo code</li>
                  <li>Send alert to SuperAdmin team</li>
                  <li>Create audit trail entry</li>
                  {suspendUsers && <li>Suspend associated user accounts</li>}
                </ul>
              </span>
            </label>
          </div>

          {error && (
            <div className="modal-error">
              <strong>Error:</strong> {error}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="modal-button modal-button--secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="modal-button modal-button--danger"
            onClick={handleFlagFraud}
            disabled={loading || !fraudReason.trim() || fraudReason.trim().length < 20 || !confirmed}
          >
            {loading ? 'Flagging...' : 'Flag as Fraud'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FlagFraudModal;
